import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';

import { selectIsLoggedInState } from '../../store/selectors';
import { withTranslation } from '../../src/i18n';
import routesWithTitle from '../../constants/routesWithTitle';
import { colors } from '../../constants/styleConstants';
import LangSelect from '../LangSelect';
import SideBarContent from '../SideBarContent';

const useStyles = makeStyles(theme => ({
  drawer: {
    backgroundColor: '#eee',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      width: 240,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: 240,
    position: 'static',
    backgroundColor: '#eee',
  },
  closeMenuButton: {
    marginLeft: 'auto',
    marginRight: 0,
  },
  fit: {
    marginTop: 30,
  },
  paperAnchorDockedLeft: {
    borderRight: '0px!important',
  },
  sideBar: {
    backgroundColor: colors.background.gray,
    fontWeight: 'normal',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  section: {
    padding: 0,
    paddingTop: 30,
    margin: 0,
    '& li': {
      listStyleType: 'none',
    },
    '&:last-child': {
      padding: '30px 0',
    },
  },
  sectionTitle: {
    padding: '18px 30px',
    margin: 0,
  },
  activeNavTab: {
    backgroundColor: colors.biscue,
    color: `${colors.primary} !important`,
    fontWeight: 'bold',
  },
  sectionItems: {
    fontWeight: 'lighter',
  },
  sectionItem: {
    color: colors.title,
    display: 'block',
    textDecoration: 'none',
    fontSize: 15,
    lineHeight: '28px',
    padding: '0 40px',
    ' &: hover': {
      backgroundColor: colors.biscue,
      color: `${colors.primary} !important`,
      fontWeight: 'normal',
    },
    '&:focus': {
      backgroundColor: colors.biscue,
      color: `${colors.primary} !important`,
      fontWeight: 'bold',
    },
  },
}));

const Sidebar = ({ t, mobileOpen, closeMobileDrawer, links, isLoggedIn }) => {
  const router = useRouter();
  const theme = useTheme();
  const classes = useStyles();
  const activeTab = router.pathname;

  const sideBarLinks = {
    introduction: {
      title: 'operabase.general.m_INTRODUCTION',
      items: [
        routesWithTitle.INTRO_OB,
        routesWithTitle.INTRO_AOS,
        routesWithTitle.INTRO_ARTISTS,
        routesWithTitle.INTRO_AGENTS,
        routesWithTitle.CONTACT_OB,
      ],
    },
    performances: {
      title: 'operabase.general.m_PERF',
      items: [
        routesWithTitle.PRODUCTIONS,
        routesWithTitle.SEASONS,
        routesWithTitle.FESTIVALS,
        routesWithTitle.HIGHLIGHTS,
        routesWithTitle.NEW_RARE,
      ],
    },
    artists: {
      title: 'operabase.general.m_ARTISTS',
      items: [
        routesWithTitle.ARTISTS,
        routesWithTitle.MANAGERS,
        routesWithTitle.COMPETITIONS,
        {
          ...routesWithTitle.CASTING_TOOL,
          isPro: true,
        },
      ],
    },
    aos: {
      title: 'operabase.general.m_OPCOS',
      items: [routesWithTitle.COMPANIES, routesWithTitle.MAPS, routesWithTitle.VENUES],
    },
    productions: {
      title: 'operabase.general.m_PROD',
      items: [routesWithTitle.RENTALS],
    },
    reference: {
      title: 'operabase.general.m_REFERENCE',
      items: [
        routesWithTitle.STATISTICS,
        {
          ...routesWithTitle.ARTICLES,
          disabled: false,
        },
      ],
    },
    accountSettings: {
      title: 'operabase.general.m_SIDEBAR_SECTION_TITLE_SETTINGS',
      items: [
        {
          ...routesWithTitle.ACCOUNT_SETTINGS,
          isShown: () => isLoggedIn,
        },
      ],
    },
    lang: {
      title: 'operabase.general.m_SIDEBAR_TRANSLATIONS',
      items: [],
      component: <LangSelect />,
    },
  };

  const sideBarData = links || sideBarLinks;

  return (
    <nav className={classes.drawer}>
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={closeMobileDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
          // ModalProps={{
          //   keepMounted: true, // Better open performance on mobile.
          // }}
        >
          <IconButton onClick={closeMobileDrawer} className={classes.closeMenuButton}>
            <CloseIcon />
          </IconButton>
          <SideBarContent t={t} sideBarData={sideBarData} activeTab={activeTab} />
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          className={classes.drawer}
          variant="permanent"
          classes={{
            paper: classes.drawerPaper,
            paperAnchorDockedLeft: classes.paperAnchorDockedLeft,
          }}
        >
          <SideBarContent t={t} sideBarData={sideBarData} activeTab={activeTab} />
        </Drawer>
      </Hidden>
    </nav>
  );
};

Sidebar.getInitialProps = async () => ({
  namespacesRequired: ['translation'],
});

Sidebar.propTypes = {
  links: PropTypes.object,
  t: PropTypes.func.isRequired,
  closeMobileDrawer: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  mobileOpen: PropTypes.bool,
};

Sidebar.defaultProps = {
  isLoggedIn: false,
};

const mapStateToProps = createStructuredSelector({
  isLoggedIn: selectIsLoggedInState(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(withTranslation('translation'), withConnect)(Sidebar);
